import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard, LoadingOverlay, useAlert } from '../../components';
import { getTransactions } from '../../services/firestoreService';
import { TRANSACTION_TYPES } from '../../config/firebase';
import { formatCurrency, formatDateTime } from '../../utils/helpers';
import './TransactionsScreen.css';

export const TransactionsScreen = () => {
    const navigate = useNavigate();
    const alert = useAlert();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    const filters = [
        { key: 'all', label: 'All' },
        { key: TRANSACTION_TYPES.RECHARGE, label: 'Recharge' },
        { key: TRANSACTION_TYPES.WITHDRAW, label: 'Withdraw' },
        { key: TRANSACTION_TYPES.PURCHASE, label: 'Purchase' },
        { key: TRANSACTION_TYPES.DAILY_INCOME, label: 'Income' },
        { key: TRANSACTION_TYPES.GIFT_CODE, label: 'Gift' },
    ];

    useEffect(() => {
        loadTransactions();
    }, []);

    const loadTransactions = async () => {
        try {
            const data = await getTransactions();
            setTransactions(data);
        } catch (error) {
            alert.showError('Error', 'Failed to load transactions');
        } finally {
            setLoading(false);
        }
    };

    const getTransactionIcon = (type) => {
        switch (type) {
            case TRANSACTION_TYPES.RECHARGE:
                return 'add_circle';
            case TRANSACTION_TYPES.WITHDRAW:
                return 'remove_circle';
            case TRANSACTION_TYPES.PURCHASE:
                return 'shopping_cart';
            case TRANSACTION_TYPES.DAILY_INCOME:
                return 'trending_up';
            case TRANSACTION_TYPES.REFERRAL_BONUS:
                return 'group_add';
            case TRANSACTION_TYPES.GIFT_CODE:
                return 'card_giftcard';
            default:
                return 'swap_horiz';
        }
    };

    const getTransactionColor = (type) => {
        switch (type) {
            case TRANSACTION_TYPES.RECHARGE:
                return '#00C853';
            case TRANSACTION_TYPES.WITHDRAW:
                return '#FF5252';
            case TRANSACTION_TYPES.PURCHASE:
                return '#3B82F6';
            case TRANSACTION_TYPES.DAILY_INCOME:
                return '#00C853';
            case TRANSACTION_TYPES.REFERRAL_BONUS:
                return '#D4AF37';
            case TRANSACTION_TYPES.GIFT_CODE:
                return '#F59E0B';
            default:
                return '#8A8AA3';
        }
    };

    const filteredTransactions = filter === 'all'
        ? transactions
        : transactions.filter(t => t.type === filter);

    if (loading) {
        return <LoadingOverlay visible message="Loading transactions..." />;
    }

    return (
        <div className="transactions-screen">
            <div className="screen-header">
                <button className="back-btn" onClick={() => navigate(-1)}>
                    <span className="material-icons">arrow_back</span>
                </button>
                <h1 className="screen-title">Transactions</h1>
                <div style={{ width: 40 }}></div>
            </div>

            {/* Filters */}
            <div className="filters-container">
                {filters.map((f) => (
                    <button
                        key={f.key}
                        className={`filter-btn ${filter === f.key ? 'active' : ''}`}
                        onClick={() => setFilter(f.key)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {/* Transactions List */}
            <div className="transactions-list">
                {filteredTransactions.length > 0 ? (
                    filteredTransactions.map((transaction) => (
                        <GlassCard key={transaction.id} className="transaction-card">
                            <div
                                className="transaction-icon"
                                style={{ backgroundColor: `${getTransactionColor(transaction.type)}15` }}
                            >
                                <span
                                    className="material-icons"
                                    style={{ color: getTransactionColor(transaction.type) }}
                                >
                                    {getTransactionIcon(transaction.type)}
                                </span>
                            </div>
                            <div className="transaction-info">
                                <span className="transaction-type">
                                    {transaction.type?.replace(/_/g, ' ').toUpperCase()}
                                </span>
                                <span className="transaction-desc">{transaction.description}</span>
                                <span className="transaction-date">
                                    {formatDateTime(transaction.createdAt)}
                                </span>
                            </div>
                            <span
                                className="transaction-amount"
                                style={{
                                    color: transaction.amount >= 0 ? '#00C853' : '#FF5252'
                                }}
                            >
                                {transaction.amount >= 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                            </span>
                        </GlassCard>
                    ))
                ) : (
                    <div className="empty-state">
                        <span className="material-icons empty-icon">receipt_long</span>
                        <h3>No Transactions</h3>
                        <p>Your transaction history will appear here</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TransactionsScreen;
